import { useState, useEffect } from 'react';
import { Link } from 'wouter';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { BadgeData, getBadges, updateBadge } from '@/lib/firebase';
import { useAuth } from '@/contexts/auth-context';
import { Award, ChevronUp, ChevronDown, ArrowLeft, Save } from 'lucide-react';

const TYPE_OPTIONS: BadgeData['type'][] = ['program', 'steps', 'streak', 'yoga', 'community', 'content'];

const getColourClasses = (colour: BadgeData['colour']) => {
  const colourMap: Record<string, string> = {
    gold: 'bg-yellow-400 text-yellow-900',
    silver: 'bg-slate-300 text-slate-700',
    bronze: 'bg-orange-600 text-orange-100',
    green: 'bg-green-500 text-green-100',
    purple: 'bg-purple-500 text-purple-100',
    blue: 'bg-blue-500 text-blue-100',
    pink: 'bg-pink-500 text-pink-100',
    yellow: 'bg-yellow-500 text-yellow-100'
  };
  return colourMap[colour] || colourMap.gold;
};

export default function BadgeOrderPage() {
  const [groupedBadges, setGroupedBadges] = useState<Record<string, BadgeData[]>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    if (user) loadBadges();
  }, [user]);

  const loadBadges = async () => {
    try {
      setLoading(true);
      const badges = await getBadges();
      const grouped: Record<string, BadgeData[]> = {};
      for (const type of TYPE_OPTIONS) {
        grouped[type] = badges
          .filter(b => b.type === type)
          .sort((a, b) => {
            const aOrder = a.sortOrder ?? 9999;
            const bOrder = b.sortOrder ?? 9999;
            return aOrder - bOrder;
          });
      }
      setGroupedBadges(grouped);
    } catch (error: any) {
      toast({ title: 'Failed to load badges', description: error.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const moveItem = (type: string, index: number, direction: 'up' | 'down') => {
    const group = [...(groupedBadges[type] || [])];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= group.length) return;
    [group[index], group[swapIndex]] = [group[swapIndex], group[index]];
    setGroupedBadges(prev => ({ ...prev, [type]: group }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updates: Promise<void>[] = [];
      for (const type of TYPE_OPTIONS) {
        const group = groupedBadges[type] || [];
        group.forEach((badge, index) => {
          if (badge.id) {
            updates.push(updateBadge(badge.id, { sortOrder: index }));
          }
        });
      }
      await Promise.all(updates);
      toast({ title: 'Order saved', description: 'Badge sort order has been updated.' });
    } catch (error: any) {
      toast({ title: 'Failed to save order', description: error.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const totalBadges = Object.values(groupedBadges).reduce((sum, g) => sum + g.length, 0);

  return (
    <div className="flex-1 p-8">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-3">
          <Link href="/badges">
            <Button variant="ghost" size="sm" className="mr-1">
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
          </Link>
          <Award className="w-8 h-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Badge Order</h1>
            <p className="text-slate-600">Set the display order of badges within each type</p>
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving || loading} className="flex items-center space-x-2">
          <Save className="w-4 h-4" />
          <span>{saving ? 'Saving...' : 'Save Order'}</span>
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-slate-600">Loading badges...</p>
        </div>
      ) : totalBadges === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Award className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600">No badges found. Add some badges first.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {TYPE_OPTIONS.map(type => {
            const group = groupedBadges[type] || [];
            if (group.length === 0) return null;
            return (
              <Card key={type}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base capitalize flex items-center gap-2">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">
                      {type}
                    </span>
                    <span className="text-slate-400 text-sm font-normal">{group.length} badge{group.length !== 1 ? 's' : ''}</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-2">
                    {group.map((badge, index) => (
                      <div
                        key={badge.id}
                        className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200"
                      >
                        <span className="text-xs text-slate-400 w-5 text-right font-mono">{index + 1}</span>
                        {badge.imageUrl ? (
                          <img
                            src={badge.imageUrl}
                            alt={badge.name}
                            className="w-9 h-9 object-cover rounded flex-shrink-0"
                          />
                        ) : (
                          <div className={`w-9 h-9 rounded flex items-center justify-center flex-shrink-0 ${getColourClasses(badge.colour)}`}>
                            <Award className="w-4 h-4" />
                          </div>
                        )}
                        <span className="flex-1 font-medium text-slate-800 text-sm">{badge.name}</span>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() => moveItem(type, index, 'up')}
                            disabled={index === 0}
                          >
                            <ChevronUp className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() => moveItem(type, index, 'down')}
                            disabled={index === group.length - 1}
                          >
                            <ChevronDown className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
